import { useState, useEffect, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { TooltipProps } from "./tooltip.props";
import { ContainerTooltip } from "./tooltip.styled";

const Tooltip: React.FC<TooltipProps> = ({ text, children, backgroundColor, textColor, width, place }) => {
	const randomTooltipID = Math.random().toString();
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);
	const isMobileScreen = screenWidth < 769;
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (!isMobileScreen) return;

		const hideTooltip = (e: Event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsTooltipOpen(false);
			}
		};

		document.addEventListener("pointerdown", hideTooltip);
		document.addEventListener("focusin", hideTooltip); // handles input focus on iOS

		return () => {
			document.removeEventListener("pointerdown", hideTooltip);
			document.removeEventListener("focusin", hideTooltip);
		};
	}, [isMobileScreen]);

	const containerProps = {
		ref: wrapperRef,
		backgroundColor,
		textColor,
		...(isMobileScreen && {
			onClick: () => setIsTooltipOpen(true)
		}),
	};

	const tooltipProps = {
		clickable: true,
		className: "tooltipStyle",
		id: randomTooltipID,
		...(isMobileScreen ? { isOpen: isTooltipOpen } : {}),
	};

	return (
		<ContainerTooltip {...containerProps}>
			{!children ? (
				<WarningIcon
					data-tooltip-id={randomTooltipID}
					data-tooltip-html={renderToStaticMarkup(text)}
					className="hover:opacity-70"
				/>
			) : (
				<div
					data-tooltip-id={randomTooltipID}
					data-tooltip-html={renderToStaticMarkup(text)}
					className="hover:opacity-70"
				>
					{children}
				</div>
			)}

			<ReactTooltip place={place} {...tooltipProps} style={width ? { width : width } : undefined}/>
		</ContainerTooltip>
	);
};

export default Tooltip;

const WarningIcon = ({ ...props }: any) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="#2C76AA"
			width="20px"
			height="20px"
			viewBox="0 0 24 24"
			{...props}>
			<path
				d="M12 3C7.04 3 3 7.04 3 12C3 16.96 7.04 21 12 21C16.96 21 21 16.96 21 12C21 7.04 16.96 3 12 3ZM12 19.5C7.86 19.5 4.5 16.14 4.5 12C4.5 7.86 7.86 4.5 12 4.5C16.14 4.5 19.5 7.86 19.5 12C19.5 16.14 16.14 19.5 12 19.5ZM14.3 7.7C14.91 8.31 15.25 9.13 15.25 10C15.25 10.87 14.91 11.68 14.3 12.3C13.87 12.73 13.33 13.03 12.75 13.16V13.5C12.75 13.91 12.41 14.25 12 14.25C11.59 14.25 11.25 13.91 11.25 13.5V12.5C11.25 12.09 11.59 11.75 12 11.75C12.47 11.75 12.91 11.57 13.24 11.24C13.57 10.91 13.75 10.47 13.75 10C13.75 9.53 13.57 9.09 13.24 8.76C12.58 8.1 11.43 8.1 10.77 8.76C10.44 9.09 10.26 9.53 10.26 10C10.26 10.41 9.92 10.75 9.51 10.75C9.1 10.75 8.76 10.41 8.76 10C8.76 9.13 9.1 8.32 9.71 7.7C10.94 6.47 13.08 6.47 14.31 7.7H14.3ZM13 16.25C13 16.8 12.55 17.25 12 17.25C11.45 17.25 11 16.8 11 16.25C11 15.7 11.45 15.25 12 15.25C12.55 15.25 13 15.7 13 16.25Z"
				fill="#2C76AA"
			/>
		</svg>
	);
};
